/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSigintSensorsRequest,
  ListSigintSensorsSecurity,
} from "../models/operations/listsigintsensors.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSigintSensorsInfiniteQuery,
  buildSigintSensorsQuery,
  prefetchSigintSensors,
  prefetchSigintSensorsInfinite,
  queryKeySigintSensors,
  queryKeySigintSensorsInfinite,
  SigintSensorsInfiniteQueryData,
  SigintSensorsPageParams,
  SigintSensorsQueryData,
} from "./sigintSensors.core.js";
export {
  buildSigintSensorsInfiniteQuery,
  buildSigintSensorsQuery,
  prefetchSigintSensors,
  prefetchSigintSensorsInfinite,
  queryKeySigintSensors,
  queryKeySigintSensorsInfinite,
  type SigintSensorsInfiniteQueryData,
  type SigintSensorsPageParams,
  type SigintSensorsQueryData,
};

export type SigintSensorsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listSensors sigint
 *
 * @remarks
 * List active sensors in deterministic ID order with each sensor's ordered signal_ids from one consistent database snapshot. The collection is [] for an unattached sensor.
 */
export function useSigintSensors(
  request?: ListSigintSensorsRequest | undefined,
  security?: ListSigintSensorsSecurity | undefined,
  options?: QueryHookOptions<SigintSensorsQueryData, SigintSensorsQueryError>,
): UseQueryResult<SigintSensorsQueryData, SigintSensorsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSigintSensorsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSensors sigint
 *
 * @remarks
 * List active sensors in deterministic ID order with each sensor's ordered signal_ids from one consistent database snapshot. The collection is [] for an unattached sensor.
 */
export function useSigintSensorsSuspense(
  request?: ListSigintSensorsRequest | undefined,
  security?: ListSigintSensorsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SigintSensorsQueryData,
    SigintSensorsQueryError
  >,
): UseSuspenseQueryResult<SigintSensorsQueryData, SigintSensorsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSigintSensorsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSensors sigint
 *
 * @remarks
 * List active sensors in deterministic ID order with each sensor's ordered signal_ids from one consistent database snapshot. The collection is [] for an unattached sensor.
 */
export function useSigintSensorsInfinite(
  request?: ListSigintSensorsRequest | undefined,
  security?: ListSigintSensorsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    SigintSensorsInfiniteQueryData,
    SigintSensorsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<SigintSensorsInfiniteQueryData, SigintSensorsPageParams>,
  SigintSensorsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    SigintSensorsInfiniteQueryData,
    SigintSensorsQueryError,
    InfiniteData<SigintSensorsInfiniteQueryData, SigintSensorsPageParams>,
    QueryKey,
    SigintSensorsPageParams
  >({
    ...buildSigintSensorsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listSensors sigint
 *
 * @remarks
 * List active sensors in deterministic ID order with each sensor's ordered signal_ids from one consistent database snapshot. The collection is [] for an unattached sensor.
 */
export function useSigintSensorsInfiniteSuspense(
  request?: ListSigintSensorsRequest | undefined,
  security?: ListSigintSensorsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    SigintSensorsInfiniteQueryData,
    SigintSensorsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<SigintSensorsInfiniteQueryData, SigintSensorsPageParams>,
  SigintSensorsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    SigintSensorsInfiniteQueryData,
    SigintSensorsQueryError,
    InfiniteData<SigintSensorsInfiniteQueryData, SigintSensorsPageParams>,
    QueryKey,
    SigintSensorsPageParams
  >({
    ...buildSigintSensorsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setSigintSensorsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SigintSensorsQueryData,
): SigintSensorsQueryData | undefined {
  const key = queryKeySigintSensors(...queryKeyBase);

  return client.setQueryData<SigintSensorsQueryData>(key, data);
}

export function invalidateSigintSensors(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "sigint", "listSensors", ...queryKeyBase],
  });
}

export function invalidateAllSigintSensors(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "sigint", "listSensors"],
  });
}
